// ChatDock — chat nổi kiểu Messenger treo góc phải, dùng được ở MỌI trang admin
// (trừ /chat full). Tắt/bật được. Mở nhiều cửa sổ: giới hạn theo bề ngang, tràn
// thì gộp thành chat-head (+N). Sống trong shared kernel (cnv-ui) → mount ở
// AppLayout (SaaS) + Layout (Core).
//
// Bundle: file này CHỈ giữ launcher + state (nhẹ). Toàn bộ phần nặng (provider +
// cửa sổ + MessageList) ở DockRoot, lazy-load khi user MỞ dock lần đầu → KHÔNG
// phình bundle chính / không tốn fetch ở trang không dùng chat.
import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Power, ChevronLeft } from 'lucide-react';
import { useTeamChatUnreadTotal } from '../../lib/teamChatUnread';
import lazyWithReload from '../../utils/lazyWithReload';

import { useLingui } from '@lingui/react/macro';

const DockRoot = lazyWithReload(() => import('./DockRoot.jsx'), 'DockRoot');

const ENABLED_KEY = 'cnv:chat_dock_enabled';
const readEnabled = () => { try { return localStorage.getItem(ENABLED_KEY) !== '0'; } catch { return true; } };
const writeEnabled = (v) => { try { localStorage.setItem(ENABLED_KEY, v ? '1' : '0'); } catch { /* noop */ } };

export default function ChatDock() {
  const {
    t
  } = useLingui();

  const location = useLocation();
  const onChatPage = /^\/chat(\/|$)/.test(location.pathname || '');
  const [enabled, setEnabled] = useState(readEnabled);
  const [opened, setOpened] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [windows, setWindows] = useState([]); // room snapshots, newest-first
  const [minimizedIds, setMinimizedIds] = useState([]);
  const [focusedId, setFocusedId] = useState(null);
  const unreadTotal = useTeamChatUnreadTotal();

  const openLauncher = useCallback(() => { setOpened(true); setListOpen((o) => !o); }, []);
  const disableDock = useCallback(() => { setEnabled(false); writeEnabled(false); setListOpen(false); }, []);

  const openRoom = useCallback((snap) => {
    if (!snap?.id) return;
    setMinimizedIds((ids) => ids.filter((id) => id !== snap.id));
    setWindows((ws) => [snap, ...ws.filter((w) => w.id !== snap.id)]); // newest-first
    setFocusedId(snap.id);
    setListOpen(false);
  }, []);
  const closeWindow = useCallback((id) => {
    setWindows((ws) => ws.filter((w) => w.id !== id));
    setMinimizedIds((ids) => ids.filter((x) => x !== id));
    setFocusedId((f) => (f === id ? null : f));
  }, []);
  const minimizeWindow = useCallback((id) => {
    setMinimizedIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  }, []);
  const restoreWindow = useCallback((id) => {
    setMinimizedIds((ids) => ids.filter((x) => x !== id));
    setWindows((ws) => { const w = ws.find((x) => x.id === id); return w ? [w, ...ws.filter((x) => x.id !== id)] : ws; });
    setFocusedId(id);
  }, []);

  if (onChatPage) return null;

  // Đã tắt → KHÔNG mất hẳn: hiện 1 tab mảnh ở mép phải để bật lại bất cứ lúc nào
  // (tắt là vĩnh viễn/persist localStorage, KHÔNG tự quay lại sau X phút).
  if (!enabled) {
    return (
      <button
        type="button"
        onClick={() => { setEnabled(true); writeEnabled(true); }}
        title={t`Hiện lại chat nổi`}
        aria-label={t`Hiện lại chat nổi`}
        className="fixed bottom-6 right-0 z-40 flex items-center justify-center w-5 h-16 rounded-l-lg bg-blue-600 text-white shadow-lg hover:w-7 hover:bg-blue-700 transition-all"
      >
        <ChevronLeft size={14} />
      </button>
    );
  }

  return (
    <>
      <Launcher unread={unreadTotal} onClick={openLauncher} onDisable={disableDock} />
      {opened && (
        <Suspense fallback={null}>
          <DockRoot
            listOpen={listOpen}
            setListOpen={setListOpen}
            windows={windows}
            minimizedIds={minimizedIds}
            focusedId={focusedId}
            onOpenRoom={openRoom}
            onCloseWindow={closeWindow}
            onMinimizeWindow={minimizeWindow}
            onRestoreWindow={restoreWindow}
            onFocusWindow={setFocusedId}
          />
        </Suspense>
      )}
    </>
  );
}

function Launcher({ unread, onClick, onDisable }) {
  const {
    t
  } = useLingui();

  return (
    <div className="fixed bottom-4 right-4 z-40 group">
      <button
        type="button"
        onClick={onClick}
        aria-label={t`Mở chat`}
        className="relative w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg hover:bg-blue-700 active:scale-95 transition"
      >
        <MessageCircle size={26} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-medium flex items-center justify-center border-2 border-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={onDisable}
        aria-label={t`Tắt chat nổi`}
        title={t`Tắt chat nổi (hiện lại ở tab mép phải màn hình)`}
        className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-gray-700 text-white items-center justify-center hidden group-hover:flex hover:bg-gray-900 transition"
      >
        <Power size={11} />
      </button>
    </div>
  );
}
